/* eslint-disable */
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  deleteEmissionFactorsByCollector,
} from "api/rec";

import {
  COLORS,
  DeleteDialog,
  H4,
  Label14Medium,
  Loading,
  useDialogControls,
} from "components";

import { useNotify } from "hooks";


import { EsgDeleteIcon, EsgLeftArrow, EsgRECPendingIcon, EsgRECUploadedIcon, PlusIcon } from "assets";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { UtilitySpecificEmissionsFactorsModal } from "./components/site-editor-form/components/UtilitySpecificEmissionsFactorsModal";
import { getFormattedDate } from "utils";
import { getCollectorById } from "api";

export const UtilitySpecificEmissionsFactors: React.FunctionComponent = () => {

  // ---- Hooks ----
  dayjs.extend(timezone);
  const navigate = useNavigate();

  // ---- Const-----
  const { collectorId } = useParams();
  const deleteDialogControls = useDialogControls();
  const notify = useNotify();

  // ---- State ----
  const [isEmissionFactorModalOpen, setEmissionFactorModalOpen] = useState(false);
  const [editEmissionFactorData, setEditEmissionFactorData] = useState(null);
  const [emissionFactorId, setEmissionFactorId] = useState()
  // ---- API's ----
  const { data: collectorData, mutateAsync: getCollectorData, isLoading: isLoading } = useMutation(getCollectorById);
  const { mutateAsync: deleteEmissionFactorsData } = useMutation(deleteEmissionFactorsByCollector);
  const emissionFactorDetails = collectorData?.emissionFactorDetails;

  // ---- Use Effects ----
  useEffect(() => {
    getCollectorData(parseInt(collectorId || ''))
  }, [collectorId])

  // ----- Table Columns ----
  const emissionFactorDetailColumn = useMemo(
    () => [
      {
        accessorKey: "providerName",
        header: "Utility Provider",
      },
      {
        accessorKey: "utilityType",
        header: "Utility Type",
      },
      {
        accessorKey: "efValue",
        header: "EF Value",
      },
      {
        accessorKey: "efUnit",
        header: "EF Unit",
      },
      {
        accessorKey: "renewableEnergy",
        header: "RE Percentage",
      },
      {
        accessorKey: "startDate",
        header: "EF Start Date",
        Cell: ({ cell }: any) =>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ ml: 1 }} component="span">
              {getFormattedDate(cell.getValue())}
            </Box>
          </Box>
      },
      {
        accessorKey: "endDate",
        header: "EF End Date",
        Cell: ({ cell }: any) =>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ ml: 1 }} component="span">
              {getFormattedDate(cell.getValue())}
            </Box>
          </Box>
      }
    ],
    [],
  ) as MRT_ColumnDef<any>[];


  // ---- Page Functions ----
  const onCloseEmissionFactorModalClick = () => {
    setEmissionFactorModalOpen(false);
    setEditEmissionFactorData(null);
  }

  const onSaveEmissionFactorModalClick = () => {
    onCloseEmissionFactorModalClick();
    getCollectorData(parseInt(collectorId || ''))
  }

  const handleEditCollector = (rowData: any) => {
    setEditEmissionFactorData(rowData);
    setEmissionFactorModalOpen(true);
  };

  const handleDeleteEmissionFactorData = () => {
    deleteEmissionFactorsData({ collectorId, emissionFactorId }, {
      onSuccess: () => {
        getCollectorData(parseInt(collectorId || ''));
        deleteDialogControls.close();
        notify.success("Deleted Successfully")
      },
      onError: (e: any) =>
        notify.error(
          e?.response?.data ?? `Some error has happen while deleting Data!`,
        ),
    })
  }

  if (isLoading) return <Loading />;
  return (
    <Box sx={{ mx: 3 }}>
      <H4 mt={4} mb={2}>
        Utility Specific Emissions Factors
      </H4>
      <Divider sx={{ mt: 2, mb: 2, backgroundColor: '#054254' }} />
      <br />
      <ButtonBase sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:active': {
          backgroundColor: 'rgba(0, 0, 0, 0.12)', // active state effect
        },
      }}
        onClick={() => navigate(-1)}
      >
        <EsgLeftArrow />
        <Label14Medium>Back to Utility Providers</Label14Medium>
      </ButtonBase>
      <Button
        sx={{ my: 2, borderRadius: '8px' }}
        variant="contained"
        onClick={() => setEmissionFactorModalOpen(true)}
        startIcon={<PlusIcon />}
      >
        Add Utility Specific Emissions Factor
      </Button>
      <br />
      <Box>
        {emissionFactorDetails && <MaterialReactTable
          muiTableContainerProps={{
            sx: {
              "& tbody tr td:first-of-type": {
                paddingLeft: "20px",
              },
              "& tbody tr:first-of-type td > p": {
                display: "flex",
                width: "100%",
                justifyContent: "center",
              },
              border: "1px solid #D9D9D9", borderRadius: '10px',
              minWidth: '100%',
              width: '100%',
              maxWidth: '100%',
              '@media (min-width: 600px)': {
                width: '100%',
              },
              '@media (min-width: 960px)': {
                width: '1200px',
              },
              '@media (min-width: 1280px)': {
                width: '1200px',
              },
              '@media (min-width: 1440px)': {
                width: '1200px',
              },
              '@media (min-width: 1680px)': {
                width: '1400px',
              },
              '@media (min-width: 2400px)': {
                width: '100%',
              },
            }
          }}
          muiTablePaperProps={{
            sx: {
              borderRadius: '10px',
              boxShadow: 'none'
            }
          }}
          muiTopToolbarProps={{
            sx: {
              display: 'none'
            }
          }}
          muiBottomToolbarProps={{
            sx: {
              display: 'none'
            }
          }}
          muiTableHeadCellProps={{
            sx: {
              lineHeight: '30px',
              alignItems: 'center',
              color: COLORS.Romance,
              fontWeight: '600',
              fontSize: '14px',
              fontStyle: "Inter",
              '& .MuiBadge-root': {
                display: 'none',
              },
            }
          }}
          muiTableHeadRowProps={{
            sx: {
              height: '40px',
              backgroundColor: '#F8FAFC',
              borderBottomColor: '#F8FAFC',
              textAlign: 'center',
              alignItems: 'center'

            }
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleEditCollector(row?.original),
            sx: {
              cursor: 'pointer',
              height: '40px',
              backgroundColor: 'white',
            },
          })}
          columns={emissionFactorDetailColumn}
          data={emissionFactorDetails}
          enablePagination={false}
          enableDensityToggle={false}
          enableRowActions
          initialState={{
            density: "compact",
          }}
          renderRowActions={({ row }) => {
            const emissionFactorId = row.original?.id;
            const navigationPath = `/utility-specific-emissionsFactors-details/${collectorId}/${emissionFactorId}`;
            return (
              <Box sx={{
                display: "flex",
                borderRight: '1px solid rgba(224,224,224,1)', height: '45px', alignItems: 'center',
              }}>
                <Tooltip arrow placement="top" title={row?.original?.s3Path ? "Document is Uploaded" : "Upload Document"}>
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={() => navigate(navigationPath)}>
                    {row?.original?.s3Path ? <EsgRECUploadedIcon /> : <EsgRECPendingIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="top" title="Delete">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={(e: any) => {
                      setEmissionFactorId(row?.original?.id)
                      e.stopPropagation();
                      deleteDialogControls.open()
                    }}
                  >
                    <EsgDeleteIcon color="red" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }}
        />}
      </Box>
      <br />

      {isEmissionFactorModalOpen && <UtilitySpecificEmissionsFactorsModal
        isOpen={
          isEmissionFactorModalOpen
        }
        editEmissionFactor={editEmissionFactorData}
        onClose={onCloseEmissionFactorModalClick}
        onSave={onSaveEmissionFactorModalClick}
        columns={emissionFactorDetailColumn}
        collectorData={collectorData}
      />}
      {deleteDialogControls.isOpen && <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={handleDeleteEmissionFactorData}
        target="Utility Specific Emission Factor"
        title="Delete Utility Specific Emission Factor documents?"
      />}
    </Box>
  );
};